import {
  Api, AuthenticationOptions, EmailUnsubscribeApi, getDefaultClient, types as api,
} from '@mesa-labs/mesa-api';
import {
  AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse,
} from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import cognitoIdentityProvider from '../../cognito';
import { RedactedVendorApi } from '@mesa-labs/mesa-api/dist/apis/redactedVendor';
import { RedactedInvoiceApi } from '@mesa-labs/mesa-api/dist/apis/redactedInvoice';
import { RedactedPaymentApi } from '@mesa-labs/mesa-api/dist/apis/redactedPayment';
import { RedactedExternalVendorApi } from '@mesa-labs/mesa-api/dist/apis/redactedExternalVendor';
import { updateTimeout429Timestamp } from '../slices/auth';
import { DateTime } from 'luxon';

export const getIdToken = async (): Promise<string | undefined> => {
  try {
    return cognitoIdentityProvider.getCurrentSession()?.getIdToken().getJwtToken();
  } catch (_) {
    return undefined;
  }
};

export const prepareHeaders = async (headers: Headers) => {
  const idToken = await getIdToken();

  if (idToken) {
    headers.set('Authorization', `Bearer ${idToken}`);
  }

  headers.set('mode', 'cors');
  headers.set('cache', 'no-cache');
  headers.set('credentials', 'same-origin');
  headers.set('Accept', 'application/json');

  return headers;
};

export const ensureIdToken = async (): Promise<string | undefined> => {
  let session = cognitoIdentityProvider.getCurrentSession();
  if (!session?.isValid()) {
    session = await cognitoIdentityProvider.refreshCurrentSession();
  }

  return session?.isValid() ? session?.getIdToken()?.getJwtToken() : undefined;
};

export const getHeaders = () => ({
  mode: 'cors',
  cache: 'no-cache',
  credentials: 'same-origin',
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

type ApiCtor<T extends Api> = new (baseURL: string, options?: AuthenticationOptions, client?: AxiosInstance) => T;

const UnauthorizedErrorsForLogout = new Set([
  'NotAuthorizedException',
]);

export const useApi = <T extends Api>(baseURL: string, Ctor: ApiCtor<T>, dispatch: ThunkDispatch<any, any, any>, authenticated = true) => {
  const client = getDefaultClient(baseURL, getHeaders());

  if (authenticated) {
    client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      const idToken = await ensureIdToken();

      if (idToken) {
        config.headers.set('Authorization', `Bearer ${idToken}`);
      }
      return config;
    });
  }

  client.interceptors.response.use(
    (response: AxiosResponse) => ({
      ...response,
      data: response.status === 404 ? undefined : response.data,
    }),
    async (err?: AxiosError) => {
      const statusCode = err?.response?.status;
      const errorName = err?.name || '';
      // Ensure that we only sign out on 403 if we are actually missing the Auth header
      if ((statusCode === 401 && UnauthorizedErrorsForLogout.has(errorName)) || (statusCode === 403 && !err?.config?.headers?.Authorization)) {
        // TODO: This is to avoid a dependency cycle with the auth slice, which also depends on vendorApi -> api
        await cognitoIdentityProvider.signOut();
        return dispatch({ type: 'auth/signOut/fulfilled' });
      }

      if (statusCode === 429 || err?.code === 'ERR_NETWORK') {
        dispatch(updateTimeout429Timestamp(DateTime.now().plus({ seconds: 60 }).toMillis()));
      } else {
        dispatch(updateTimeout429Timestamp(0));
      }

      throw api.ApiError.fromAxiosError(err);
    },
  );

  return new Ctor(baseURL, undefined, client);
};

export const useVendorApi = (dispatch: ThunkDispatch<any, any, any>) => useApi(CONFIG.api.vendorUrl, RedactedVendorApi, dispatch);
export const useInvoiceApi = (dispatch: ThunkDispatch<any, any, any>) => useApi(CONFIG.api.invoiceUrl, RedactedInvoiceApi, dispatch);
export const usePaymentApi = (dispatch: ThunkDispatch<any, any, any>) => useApi(CONFIG.api.paymentUrl, RedactedPaymentApi, dispatch);
export const useExternalVendorApi = (dispatch: ThunkDispatch<any, any, any>) => useApi(CONFIG.api.externalVendorUrl, RedactedExternalVendorApi, dispatch);

class UnauthenticatedEmailUnsubscribeApi extends EmailUnsubscribeApi {
  constructor(baseURL: string, _options?: AuthenticationOptions, client?: AxiosInstance) {
    super(baseURL, client);
  }
}

export const useEmailUnsubscribeApi = (dispatch: ThunkDispatch<any, any, any>) => useApi(CONFIG.api.vendorUrl, UnauthenticatedEmailUnsubscribeApi, dispatch, false);
